/**
 * Вероятностный анализ сроков (ПЛ-23): прогоны Монте-Карло по трём оценкам длительности; окончание с
 * вероятностью 50, 80 и 90 %, вероятность уложиться в срок, распределение и индекс критичности работ.
 */
import type { ScheduleView } from '@epc/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { ErrorBox } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from './LField';
import { fmtMoment } from './model';

interface Risk {
  iterations: number;
  withEstimates: number;
  finishPlan: string;
  finish: { p10: string; p50: string; p80: string; p90: string };
  mustFinishBy: string | null;
  onTimeProbability: number;
  mustProbability: number | null;
  histogram: Array<{ day: string; count: number }>;
  milestones: Array<{ id: string; code: string; name: string; plan: string; p50: string; p80: string }>;
  criticality: Array<{ id: string; code: string; name: string; index: number }>;
  stoppedEarly: boolean;
}
const SPREADS = {
  none: { title: 'Только работы со своими оценками', v: null },
  low: { title: 'Остальным: −10 % … +20 %', v: { opt: 0.9, pess: 1.2 } },
  mid: { title: 'Остальным: −10 % … +40 %', v: { opt: 0.9, pess: 1.4 } },
  high: { title: 'Остальным: −15 % … +80 %', v: { opt: 0.85, pess: 1.8 } },
} as const;
const pc = (x: number) => `${Math.round(x * 100)} %`;
const dd = (s: string) => s.slice(0, 10).split('-').reverse().join('.');

export function RiskPanel({ view }: { view: ScheduleView }) {
  const [spread, setSpread] = useState<keyof typeof SPREADS>('mid');
  const [iterations, setIterations] = useState(500);
  const m = useMutation({
    mutationFn: () =>
      api<Risk>(`/schedules/${view.schedule.id}/risk`, {
        body: { iterations, spread: SPREADS[spread].v },
      }),
  });
  const r = m.data;
  const max = r ? Math.max(1, ...r.histogram.map((h) => h.count)) : 1;
  return (
    <div className="flex flex-col gap-4">
      <Panel title="Вероятностный анализ сроков">
        <p className="mb-3 text-[13px] text-ink-2">
          Длительность каждой работы берётся случайно между оптимистичной и пессимистичной оценкой (поля в
          карточке работы), наиболее вероятная — по графику. Расчёт повторяется сотни раз тем же ядром; график
          не меняется.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <LField label="Работы без своих оценок">
            <Select
              value={spread}
              onChange={(e) => setSpread(e.target.value as keyof typeof SPREADS)}
              className="max-md:h-11"
            >
              {Object.entries(SPREADS).map(([k, x]) => (
                <option key={k} value={k}>
                  {x.title}
                </option>
              ))}
            </Select>
          </LField>
          <LField label="Прогонов">
            <Select
              value={String(iterations)}
              onChange={(e) => setIterations(Number(e.target.value))}
              className="max-md:h-11"
            >
              {[200, 500, 1000, 2000].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </LField>
          <Button variant="primary" loading={m.isPending} onClick={() => m.mutate()} className="max-md:h-11">
            Посчитать
          </Button>
        </div>
        {m.isError ? <ErrorBox error={m.error} /> : null}
      </Panel>
      {r ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {(
              [
                ['По графику', r.finishPlan],
                ['С вероятностью 50 %', r.finish.p50],
                ['80 %', r.finish.p80],
                ['90 %', r.finish.p90],
              ] as const
            ).map(([t, v]) => (
              <div key={t} className="rounded-md border border-line bg-surface px-3 py-2">
                <div className="text-[12px] text-ink-2">{t}</div>
                <div className="text-[18px] font-semibold">{fmtMoment(v)}</div>
              </div>
            ))}
          </div>
          <p className="text-[14px]">
            Уложиться в окончание по графику: <b>{pc(r.onTimeProbability)}</b>
            {r.mustProbability != null && r.mustFinishBy ? (
              <>
                {' '}
                · в директивный срок {fmtMoment(r.mustFinishBy)}: <b>{pc(r.mustProbability)}</b>
              </>
            ) : null}
            <span className="ml-2 text-ink-2">
              прогонов {r.iterations}, работ со своими оценками {r.withEstimates}
              {r.stoppedEarly ? ', расчёт остановлен по времени' : ''}
            </span>
          </p>
          {r.histogram.length > 1 ? (
            <Panel title="Распределение окончания">
              <svg
                viewBox={`0 0 ${r.histogram.length * 12} 100`}
                className="h-40 w-full"
                preserveAspectRatio="none"
                role="img"
                aria-label="Сколько прогонов закончилось в каждый день"
              >
                {r.histogram.map((h, i) => (
                  <rect
                    key={h.day}
                    x={i * 12 + 1}
                    width={10}
                    y={100 - (h.count / max) * 96}
                    height={(h.count / max) * 96}
                    fill={h.day <= r.finish.p80.slice(0, 10) ? 'var(--color-ink-2)' : 'var(--color-danger)'}
                  >
                    <title>
                      {dd(h.day)}: {h.count}
                    </title>
                  </rect>
                ))}
              </svg>
              <div className="flex justify-between text-[12px] text-ink-2">
                <span>{dd(r.histogram[0]!.day)}</span>
                <span>красным — позже даты с вероятностью 80 %</span>
                <span>{dd(r.histogram.at(-1)!.day)}</span>
              </div>
            </Panel>
          ) : null}
          {r.milestones.length ? (
            <Panel title="Вехи">
              <ul className="divide-y divide-line text-[14px]">
                {r.milestones.map((x) => (
                  <li key={x.id} className="flex flex-wrap justify-between gap-2 py-1.5">
                    <span>
                      {x.code} {x.name}
                    </span>
                    <span className="num text-ink-2">
                      по графику {fmtMoment(x.plan)} · 50 % {fmtMoment(x.p50)} · 80 % {fmtMoment(x.p80)}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}
          <Panel title="Индекс критичности" meta="в скольких прогонах работа была критической">
            <ul className="divide-y divide-line text-[14px]">
              {r.criticality.map((x) => (
                <li key={x.id} className="flex items-center justify-between gap-2 py-1.5">
                  <span className="min-w-0 truncate">
                    {x.code} {x.name}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-24 rounded bg-surface-2">
                      <span className="block h-2 rounded bg-danger" style={{ width: `${x.index * 100}%` }} />
                    </span>
                    <span className="num w-12 text-right">{pc(x.index)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        </>
      ) : null}
    </div>
  );
}
